import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { coinsService } from '@/services/coinsService';
import { openCheckoutUrl } from '@/lib/payments/openCheckout';

export interface CoinWallet {
  balance: number;
  lifetime_earned: number;
  lifetime_spent: number;
}

export interface CoinTransaction {
  id: string;
  delta: number;
  kind: 'earn' | 'spend' | 'topup' | 'stipend' | 'admin_adjust' | 'refund';
  source: string;
  ref_id: string | null;
  balance_after: number;
  metadata: Record<string, unknown>;
  created_at: string;
}

export interface EconomyConfig {
  xp_per_coin: number;
  pro_monthly_stipend: number;
  earn_rewards: Record<string, number>;
  feature_prices: Record<string, number>;
  coin_packs: Array<{
    id: string;
    coins: number;
    label: string;
    stripe_price_id: string | null;
  }>;
}

export function useCoinWallet() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['coin-wallet', user?.id],
    queryFn: async (): Promise<CoinWallet> => {
      if (!user?.id) return { balance: 0, lifetime_earned: 0, lifetime_spent: 0 };
      const { data } = await supabase
        .from('user_coins')
        .select('balance, lifetime_earned, lifetime_spent')
        .eq('user_id', user.id)
        .maybeSingle();
      return data ?? { balance: 0, lifetime_earned: 0, lifetime_spent: 0 };
    },
    enabled: !!user?.id,
    staleTime: 15_000,
  });
}

export function useCoinTransactions(limit = 25) {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['coin-transactions', user?.id, limit],
    queryFn: async (): Promise<CoinTransaction[]> => {
      if (!user?.id) return [];
      const { data } = await supabase
        .from('coin_transactions')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(limit);
      return (data ?? []) as CoinTransaction[];
    },
    enabled: !!user?.id,
    staleTime: 15_000,
  });
}

export function useEconomyConfig() {
  return useQuery({
    queryKey: ['economy-config'],
    queryFn: async (): Promise<EconomyConfig | null> => {
      const { data } = await supabase
        .from('coin_economy_config')
        .select('xp_per_coin, pro_monthly_stipend, earn_rewards, feature_prices, coin_packs')
        .eq('id', 1)
        .maybeSingle();
      return data as EconomyConfig | null;
    },
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Total XP redeemed for Coins. Subtracted from displayed XP totals so the
 * leaderboard stays honest.
 */
export function useRedeemedXp() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['coin-xp-redemptions', user?.id],
    queryFn: async (): Promise<number> => {
      if (!user?.id) return 0;
      const { data } = await supabase
        .from('coin_xp_redemptions')
        .select('xp_redeemed')
        .eq('user_id', user.id);
      return (data ?? []).reduce((s, r: any) => s + (r.xp_redeemed ?? 0), 0);
    },
    enabled: !!user?.id,
    staleTime: 30_000,
  });
}

/** Combined hook with mutations for spending / topping up / redeeming. */
export function useCoins() {
  const wallet = useCoinWallet();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['coin-wallet', user?.id] });
    queryClient.invalidateQueries({ queryKey: ['coin-transactions', user?.id] });
    queryClient.invalidateQueries({ queryKey: ['coin-xp-redemptions', user?.id] });
  };

  const spend = useMutation({
    mutationFn: async ({ feature, ref_id }: { feature: string; ref_id?: string }) => {
      const result = await coinsService.spend(feature, ref_id);
      if (!result.ok) throw new Error(result.reason || result.error || 'Spend failed');
      return result;
    },
    onSuccess: invalidate,
  });

  const redeemXp = useMutation({
    mutationFn: async (xp_amount: number) => {
      const result = await coinsService.redeemXp(xp_amount);
      if (!result.ok) throw new Error(result.reason || result.error || 'Redemption failed');
      return result;
    },
    onSuccess: invalidate,
  });

  const topup = useMutation({
    mutationFn: async (pack_id: string) => {
      const result = await coinsService.startTopup(pack_id);
      if (result.error) throw new Error(result.error);
      if (result.url) openCheckoutUrl(result.url);
      return result;
    },
  });

  return {
    balance: wallet.data?.balance ?? 0,
    lifetimeEarned: wallet.data?.lifetime_earned ?? 0,
    lifetimeSpent: wallet.data?.lifetime_spent ?? 0,
    isLoading: wallet.isLoading,
    refetch: wallet.refetch,
    spend,
    redeemXp,
    topup,
  };
}